import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Heart, 
  Users, 
  Camera, 
  Sparkles, 
  ArrowRight, 
  Award, 
  Check,
  Star,
  Quote,
  Phone,
  Calendar,
  MapPin,
  PartyPopper,
  UtensilsCrossed
} from 'lucide-react'
import EventGallery from '../components/EventGallery'
import { getImagesForFilter } from '../data/galleryImages'

// Statistiken
const stats = [
  { number: '1000', label: 'Hochzeiten', icon: Heart },
  { number: '25+', label: 'Jahre Erfahrung', icon: Award },
  { number: '400', label: 'Max. Gäste', icon: Users },
  { number: '5', label: 'Locations', icon: MapPin }
]

// Features
const features = [
  {
    icon: Award,
    title: '25+ Jahre Expertise',
    description: 'Über 1000 perfekte Hochzeiten seit 1998 – wir wissen, worauf es ankommt.'
  },
  {
    icon: Camera,
    title: 'Traumhafte Kulisse',
    description: 'Historischer Innenhof, elegante Räume & einzigartige Foto-Locations.'
  },
  {
    icon: Users,
    title: '30-400 Gäste',
    description: 'Vom intimen Dinner bis zur großen Feier – flexibel für jede Größe.'
  },
  {
    icon: Sparkles,
    title: 'Rundum-Sorglos',
    description: 'Catering, Technik, Dekoration & Service – alles aus einer Hand.'
  }
]

// Leistungen
const services = [
  'Trauungszeremonie im historischen Innenhof',
  'Professionelle Ton- & Lichttechnik',
  'DJ-Equipment & große Tanzfläche',
  'Flexible Tisch- & Raumanordnung',
  'Wetterunabhängige Außenbereiche',
  'Parkplätze für alle Gäste',
  'Barrierefreier Zugang',
  'Premium-Service-Team',
  'Keine Lautstärkeeinschränkungen',
  'Feiern bis Open End!'
]

// Galerie: Fest-Scheune-Bilder (Hochzeitsfotos aus der Scheune) aus zentraler Galerie
const hochzeitenGalleryImages = getImagesForFilter('hochzeit').map((img) => ({
  src: img.src,
  alt: img.title,
}))

export default function Hochzeiten() {
  const heroRef = useRef<HTMLDivElement>(null)
  
  const { scrollYProgress } = useScroll()
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0])
  const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 1.1])

  return (
    <div className="bg-[#0a0a0a]">
      
      {/* ════════════════════════════════════════════════════════════
          HERO SECTION - Compact Banner
      ════════════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative h-[50vh] min-h-[400px] max-h-[550px] w-full overflow-hidden">
        {/* Image Background */}
        <motion.div style={{ scale: heroScale }} className="absolute inset-0">
          <img
            src="/assets/heros/hochzeit_hero.JPG"
            alt="Hochzeit bei Hückels May"
            className="absolute inset-0 w-full h-full object-cover object-center"
            style={{ filter: 'brightness(0.3)' }}
          />
        </motion.div>

        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-[#0a0a0a]" />
        
        {/* Ruby Glow */}
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-brand-800/10 rounded-full blur-[120px]" />
        
        {/* Hero Content */}
        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6 pt-16"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-4"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full">
              <Heart className="w-3 h-3 text-brand-400" fill="currentColor" />
              <span className="text-white/70 text-xs uppercase tracking-[0.25em]">Über 1000 Traumhochzeiten</span>
            </div>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light tracking-tight leading-[1.1] mb-4"
          >
            Eure Hochzeit. <span className="italic text-white/70">Besucht uns, wenn Ihr Euch traut!</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="text-white/50 text-sm md:text-base max-w-xl mb-6 leading-relaxed"
          >
            Geschichte und Moderne verschmelzen zu Ihrer perfekten Traumhochzeit im historischen Ambiente von 1855.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="flex flex-row gap-3"
          >
            <Link to="/kontakt">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-6 py-2.5 bg-white text-black text-xs uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500"
              >
                Besichtigung buchen
              </motion.button>
            </Link>
            <Link to="/galerie">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-6 py-2.5 border border-white/30 text-white text-xs uppercase tracking-[0.2em] font-bold rounded-full hover:bg-white/10 transition-all duration-500 flex items-center gap-2"
              >
                Galerie
                <ArrowRight className="w-3 h-3" />
              </motion.button>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          STATS BAR
      ════════════════════════════════════════════════════════════ */}
      <section className="border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="py-10 md:py-12 px-6 text-center border-r border-white/5 last:border-r-0 group hover:bg-white/[0.02] transition-colors flex flex-col items-center justify-center h-[140px]"
              >
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-800/10 border border-brand-800/20 mb-3 group-hover:scale-110 transition-transform">
                  <stat.icon className="w-4 h-4 text-brand-400" />
                </div>
                <p className="text-white text-sm font-medium mb-1">{stat.number}</p>
                <p className="text-white/40 text-xs">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          FEATURES GRID
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 md:py-32">
        <div className="absolute top-20 right-20 w-[400px] h-[400px] bg-brand-800/5 rounded-full blur-[100px]" />
        
        <div className="max-w-[1400px] mx-auto px-6 relative z-10">
          {/* Header */}
          <div className="text-center mb-16">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4"
            >
              Warum Paare uns vertrauen
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light"
            >
              Ihr schönster Tag, <span className="italic text-white/60">perfekt geplant</span>
            </motion.h2>
          </div>

          {/* Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group"
              >
                <div className="h-full p-8 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-brand-800/30 hover:bg-white/[0.04] transition-all duration-500">
                  <div className="w-14 h-14 rounded-xl bg-brand-800/10 border border-brand-800/20 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-brand-800/20 transition-all duration-500">
                    <item.icon className="w-6 h-6 text-brand-400" />
                  </div>
                  <h3 className="font-serif text-xl text-white font-light mb-3">{item.title}</h3>
                  <p className="text-white/40 text-sm leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          SERVICES - Two Column Layout
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 md:py-32 border-t border-white/5">
        <div className="absolute bottom-20 left-20 w-[400px] h-[400px] bg-brand-900/5 rounded-full blur-[100px]" />
        
        <div className="max-w-[1400px] mx-auto px-6 relative z-10">
          {/* Feature Image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16 relative rounded-2xl overflow-hidden h-[300px] md:h-[400px] border border-white/10"
          >
            <img
              src="/home_pics/hochzeit.jpeg"
              alt="Hochzeit bei Hückels May"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left - Title */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:sticky lg:top-32"
            >
              <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4">Ihr Hochzeitspaket</p>
              <h2 className="font-serif text-4xl md:text-5xl text-white font-light mb-6 leading-tight">
                Alles für Ihren<br />
                <span className="italic text-white/60">perfekten Tag</span>
              </h2>
              <div className="h-px w-20 bg-brand-800 mb-6" />
              <p className="text-white/40 leading-relaxed mb-8">
                Transparent, fair, professionell – wir kümmern uns um jedes Detail, damit Sie Ihren Tag genießen können.
              </p>
              <Link to="/kontakt">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-8 py-3 bg-brand-800 text-white text-xs uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-700 transition-all duration-500 flex items-center gap-3"
                >
                  Angebot anfordern
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </Link>
            </motion.div>

            {/* Right - Services List */}
            <div className="space-y-2">
              {services.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="flex items-center gap-4 p-4 rounded-xl border-l-2 border-brand-800/20 hover:border-brand-800 hover:bg-white/[0.02] transition-all duration-300"
                >
                  <Check className="w-5 h-5 text-brand-400 flex-shrink-0" />
                  <p className="text-white/80">{item}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          HOCHZEITSMESSE - Highlight vor Bewertungen
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/10 via-transparent to-transparent" />
        <div className="max-w-[1400px] mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 md:gap-14 items-center">
            
            {/* Left: Flyer */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-white/10 bg-[#151515] shadow-2xl">
                <img
                  src="/assets/messe_flyer.jpeg"
                  alt="Flyer: Liebevoll – Die Hochzeitsmesse"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    if (e.currentTarget.src !== '/assets/brunch/brunch_vorschau_fallback.jpg') {
                      e.currentTarget.src = '/assets/brunch/brunch_vorschau_fallback.jpg'
                    }
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              </div>
            </motion.div>

            {/* Right: Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-800/10 border border-brand-800/20 rounded-full mb-5 backdrop-blur-sm">
                <Sparkles className="w-3.5 h-3.5 text-brand-400" />
                <span className="text-brand-400 text-xs uppercase tracking-[0.3em] font-semibold">Hochzeitsmesse</span>
              </div>

              <h2 className="font-serif text-3xl md:text-4xl text-white font-light mb-4 leading-tight">
                Liebevoll – Die Hochzeitsmesse<br />
                <span className="italic text-white/70">mit Herz und Schwung</span>
              </h2>

              <div className="flex flex-wrap items-center gap-4 mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-2 bg-white/5 rounded-full border border-white/10">
                  <Calendar className="w-4 h-4 text-brand-400" />
                  <span className="text-white/70 text-sm">12. April</span>
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-2 bg-white/5 rounded-full border border-white/10">
                  <MapPin className="w-4 h-4 text-brand-400" />
                  <span className="text-white/70 text-sm">Landgut Hückels May</span>
                </div>
              </div>

              <p className="text-white/50 text-base leading-relaxed mb-6">
                Besucht uns, wenn Ihr Euch traut! Am 12. April laden die zauberhaften Mädels von liebevoll – 
                Melanie & Martina gemeinsam mit uns in unser wunderschönes Landgut Hückels May ein – zu einer 
                Hochzeitsmesse, die alles andere als gewöhnlich wird.
              </p>

              <p className="text-white/60 text-lg leading-relaxed mb-6">
                Seid bereit für eine Messe mit Event-Charakter, Leichtigkeit und ganz viel Gefühl!
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="bg-[#151515] border border-white/5 rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <Heart className="w-5 h-5 text-brand-400 mt-1" />
                    <div>
                      <p className="text-white text-sm font-medium mb-1">Über 20 Aussteller</p>
                      <p className="text-white/50 text-sm">Dekoration, Fotografie, Styling, Musik & mehr.</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#151515] border border-white/5 rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-brand-400 mt-1" />
                    <div>
                      <p className="text-white text-sm font-medium mb-1">Landgut erleben</p>
                      <p className="text-white/50 text-sm">Charme, stilvolles Ambiente, einzigartiger Innenhof.</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#151515] border border-white/5 rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <PartyPopper className="w-5 h-5 text-brand-400 mt-1" />
                    <div>
                      <p className="text-white text-sm font-medium mb-1">Mr. Barfly live</p>
                      <p className="text-white/50 text-sm">Mega-Musik im Innenhof – direkt bei der Arbeit erleben.</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#151515] border border-white/5 rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <UtensilsCrossed className="w-5 h-5 text-brand-400 mt-1" />
                    <div>
                      <p className="text-white text-sm font-medium mb-1">Essen & Drinks</p>
                      <p className="text-white/50 text-sm">Außenküche, Bar & entspannte Atmosphäre.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6">
                <p className="text-white/70 text-sm uppercase tracking-[0.2em] mb-3">Wir wollen</p>
                <div className="grid sm:grid-cols-2 gap-2 text-white/70 text-sm">
                  <div>Keine steife Messe.</div>
                  <div>Emotionen.</div>
                  <div>Begegnungen.</div>
                  <div>Schwung.</div>
                </div>
              </div>

              <div className="space-y-2 text-white/60 text-sm mb-6">
                <p className="text-white/70 font-medium">Bei uns seid ihr richtig, wenn ihr…</p>
                <p>• bereits bei uns geheiratet habt und bei BBQ & Drinks in Erinnerung schwelgen möchtet,</p>
                <p>• eure Hochzeit bei uns plant,</p>
                <p>• noch unentschlossen seid oder einfach für Drinks & BBQ bei coolen Beats eine gute Zeit haben möchtet,</p>
                <p>• im Landgut feiern möchtet oder tolle Dienstleister sucht.</p>
              </div>

              <p className="text-white/50 text-base leading-relaxed">
                Diese Hochzeitsmesse ist nicht nur eine Ausstellung – sie ist ein Erlebnis. 
                Lasst uns gemeinsam das Landgut mit Leben füllen und frischen Schwung ins Hückels May bringen. 
                Wir freuen uns riesig auf euch!
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          TESTIMONIAL
      ════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Quote className="w-12 h-12 text-brand-800/30 mx-auto mb-8" />
            
            <blockquote className="font-serif text-2xl md:text-3xl text-white font-light leading-relaxed mb-8 italic">
              "Ich feierte am Samstag hier meine perfekte Hochzeit!! Es seien nur einige der durchweg positiven Kriterien genannt : Beratung im Vorfeld, Transparenz bezüglich Finanzen, Flexibilität bei individuellen Wünschen, Ambiente, Sauberkeit, frisch zubereitetes und schmackhaftes Essen, freundliche und zuvorkommende Bedienung... Danke!"
            </blockquote>
            
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Heart key={i} className="w-5 h-5 text-brand-400 fill-brand-400" />
              ))}
            </div>
            
            <p className="text-white/40 text-sm uppercase tracking-[0.2em]">
              Paula Beusch
            </p>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          EVENT-GALERIE – HIGHLIGHTS HOCHZEITEN
      ════════════════════════════════════════════════════════════ */}
      <EventGallery
        title="Hochzeitsimpressionen bei Hückels May"
        subtitle="Einblicke in echte Hochzeiten – damit Sie sehen, wie Ihr Tag bei uns aussehen kann."
        images={hochzeitenGalleryImages}
      />

      {/* ════════════════════════════════════════════════════════════
          CTA SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="py-12 md:py-20">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-800/5 rounded-full blur-[150px]" />
        
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-800/10 border border-brand-800/20 mb-8">
              <Calendar className="w-7 h-7 text-brand-400" />
            </div>
            
            <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-6">Beliebte Termine schnell vergeben</p>
            
            <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl text-white font-light mb-8 leading-tight">
              Sichern Sie sich Ihren<br />
              <span className="italic text-white/60">Wunschtermin</span>
            </h2>
            
            <p className="text-white/40 text-lg mb-10 max-w-2xl mx-auto">
              Die Monate Mai - September sind oft 12-18 Monate im Voraus vergeben. Vereinbaren Sie jetzt Ihre kostenlose Besichtigung.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/kontakt">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-10 py-4 bg-white text-black text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500 flex items-center gap-3"
                >
                  <Phone className="w-4 h-4" />
                  Kostenlos besichtigen
                </motion.button>
              </Link>
            </div>
            
            <p className="mt-8 text-white/30 text-xs uppercase tracking-[0.2em]">
              Unverbindlich · Persönliche Beratung
            </p>
          </motion.div>
        </div>
      </section>

    </div>
  )
}
